import { create } from 'zustand';
import { VITAL_PARAMETERS } from '../data/vitals';
import { loadCustomScenarios, saveCustomScenarios } from '../data/customScenarios';
import { applyVentSettingChange, deriveMechanics } from '../engine/ventEngine';

// Seed the store from the medical-reference vital parameters (single source of
// truth, validated by `npm run check:medical`). Deep-cloned so runtime mutation
// of current/target never writes back into the shared reference object.
const initialVitalState = structuredClone(VITAL_PARAMETERS);

export const useSimStore = create((set, get) => ({
  vitals: initialVitalState,

  // Setters for individual vitals
  setVital: (vitalName, updates) =>
    set((state) => ({
      vitals: {
        ...state.vitals,
        [vitalName]: { ...state.vitals[vitalName], ...updates },
      },
    })),

  // Set target (and current immediately when transitionDuration is 0)
  // Only start interpolation loop on the first change; subsequent changes just update targets
  setVitalTarget: (vitalName, targetValue) =>
    set((state) => {
      const immediate = state.transitionDuration === 0;
      const isFirstChange = !state.isTransitioning; // Only true on the very first call
      return {
        vitals: {
          ...state.vitals,
          [vitalName]: {
            ...state.vitals[vitalName],
            target: targetValue,
            ...(immediate ? { current: targetValue } : {}),
          },
        },
        // Only set duration and flag on the FIRST change to avoid resetting the interpolation timer
        ...(immediate || !isFirstChange ? {} : {
          currentTransitionDuration: state.transitionDuration,
          isTransitioning: true,
        }),
      };
    }),

  // Update current (for interpolation during transitions)
  setVitalCurrent: (vitalName, currentValue) =>
    set((state) => ({
      vitals: {
        ...state.vitals,
        [vitalName]: { ...state.vitals[vitalName], current: currentValue },
      },
    })),

  // Bulk update vitals (for scenario application).
  // When transitionDuration is 0 (immediate), snaps current = target in the same
  // atomic set() call so a single state change triggers one BroadcastChannel message.
  // When transitionDuration > 0, only target is updated; the transition engine
  // interpolates current over time and broadcasts each intermediate state.
  //
  // durationOverride — optional per-call ramp speed (ms). Used by interventions so
  // each drug/fluid can have its own physiological onset rate without changing the
  // user's global TransitionControls selection. When omitted, falls back to the
  // global transitionDuration set by the instructor.
  applyVitals: (vitalUpdates, durationOverride) =>
    set((state) => {
      const effective = durationOverride !== undefined ? durationOverride : state.transitionDuration;
      const immediate = effective === 0;
      return {
        vitals: {
          ...state.vitals,
          ...Object.entries(vitalUpdates).reduce((acc, [key, targetValue]) => {
            acc[key] = {
              ...state.vitals[key],
              target: targetValue,
              ...(immediate ? { current: targetValue } : {}),
            };
            return acc;
          }, {}),
        },
        // Store the effective duration so the interpolation loop uses it, not the
        // global transitionDuration (which stays unchanged for the UI selection).
        currentTransitionDuration: effective,
        // If transitioning, mark it as started so the interpolation loop kicks in
        isTransitioning: !immediate,
      };
    }),

  // Update alarm thresholds
  setAlarmThresholds: (vitalName, { alarmLow, alarmHigh }) =>
    set((state) => ({
      vitals: {
        ...state.vitals,
        [vitalName]: { ...state.vitals[vitalName], alarmLow, alarmHigh },
      },
    })),

  // Reset all vitals to initial state
  resetVitals: () => set({ vitals: initialVitalState }),

  // Bulk-set vitals for case REPLAY: current === target (no transition), plus the
  // NIBP last-measured display. `values` is a plain map { HR: 80, SpO2: 99, … }.
  setReplayVitals: (values) =>
    set((state) => {
      const vitals = { ...state.vitals };
      Object.entries(values).forEach(([k, v]) => {
        if (vitals[k] && typeof v === 'number' && Number.isFinite(v)) {
          vitals[k] = { ...vitals[k], current: v, target: v };
        }
      });
      const nibp = (Number.isFinite(values.NIBP_Sys) && Number.isFinite(values.NIBP_Dia))
        ? { sys: Math.round(values.NIBP_Sys), dia: Math.round(values.NIBP_Dia) }
        : state.nibpLastMeasured;
      return { vitals, nibpLastMeasured: nibp };
    }),

  // Rhythm / waveform morphology override
  // 'normal' = standard sinus rhythm; 'vf' = ventricular fibrillation
  rhythm: 'normal',
  prevRhythm: 'normal',
  // Changing the rhythm records the rhythm being left and resets the cross-fade,
  // so the monitor eases into the new morphology over the transition window
  // instead of snapping (see waveformEngine.generateWaveformBuffer).
  setRhythm: (rhythm) =>
    set((state) => (rhythm === state.rhythm ? {} : { prevRhythm: state.rhythm, rhythm, rhythmProgress: 0 })),

  // Rhythm cross-fade progress: 0 = fully prevRhythm … 1 = fully rhythm. The
  // instructor ramps it over the transition duration; synced to the monitor.
  rhythmProgress: 1,
  setRhythmProgress: (p) => set({ rhythmProgress: Math.max(0, Math.min(1, p)) }),
  // Raw apply from the sync subscriber — does NOT reset the cross-fade.
  applyRhythmSync: (rhythm, prevRhythm, rhythmProgress) =>
    set({ rhythm, prevRhythm: prevRhythm ?? rhythm, rhythmProgress: rhythmProgress ?? 1 }),

  // Capnogram waveform morphology: 'normal' | 'rebreathing' | 'curare_cleft' | 'obstructed'
  capnoMorphology: 'normal',
  setCapnoMorphology: (morphology) => set({ capnoMorphology: morphology }),

  // Active scenario modifier (e.g. 'hypovolemia'), or null if none.
  // Modifiers are persistent pathophysiological states that shift vital baselines
  // and alter how interventions respond pharmacodynamically.
  activeModifier: null,
  setActiveModifier: (key) => set({ activeModifier: key }),

  // Active scenario key (e.g. 'anaphylaxis'), or null if none.
  // Used by the intervention engine to select scenario-aware pharmacodynamic
  // deltas (e.g. epinephrine in anaphylaxis reverses tachycardia instead of
  // worsening it, and normalises capnogram morphology when bronchospasm resolves).
  activeScenario: null,
  setActiveScenario: (key) => set({ activeScenario: key }),

  // Patient metadata — used for NRP/PALS scenarios where age and weight
  // are clinically significant and should appear on the monitor display.
  // patientAge is a free-form string (e.g. "28 weeks", "6 months", "4 years").
  // patientWeight is a number in kg (or null when not set).
  // patientName / patientId appear in the monitor's top patient bar.
  patientName: 'DEMO PATIENT',
  patientId: '',
  patientAge: null,
  patientWeight: null,
  setPatientInfo: (age, weight) => set({ patientAge: age || null, patientWeight: weight || null }),
  // Generic single-field setter for demographics (name / id)
  setPatientField: (key, value) => set({ [key]: value }),

  // Audio state
  audioMuted: false,
  setAudioMuted: (muted) => set({ audioMuted: muted }),

  // Transition state — instructor-set ramp duration (ms) for scenarios/drugs.
  // Driven by the Transition Speed slider (0 = instant … 120 s). Default is a
  // gentle 45 s so changes are gradual out of the box.
  transitionDuration: 45000,
  setTransitionDuration: (duration) => set({ transitionDuration: duration }),
  // currentTransitionDuration — the duration actually used by the interpolation loop.
  // Set atomically by applyVitals so per-intervention overrides don't change the UI selection.
  currentTransitionDuration: 45000,
  isTransitioning: false,
  setIsTransitioning: (transitioning) => set({ isTransitioning: transitioning }),

  // Active alarms (array of { vitalName, level: 'advisory'|'warning'|'crisis', value })
  alarms: [],
  setAlarms: (alarms) => set({ alarms }),

  // Neuromuscular monitoring (Train of Four)
  // tofCount: 0–4 visible twitches; tofRatio: T4/T1 as 0–100%
  tofCount: 4,
  tofRatio: 100,
  setTof: (count, ratio) => set({ tofCount: count, tofRatio: ratio }),

  // BIS (Bispectral Index) — processed EEG depth-of-anaesthesia index 0–100
  bisValue: 98,
  setBIS: (value) => set({ bisValue: value }),

  // Diagnostic media popup shown on the monitor (X-ray, POCUS clip, etc.)
  // null when nothing is shown; otherwise { id, type: 'image'|'video', src, label }
  activeMedia: null,
  setActiveMedia: (media) => set({ activeMedia: media }),

  // Cognitive-aid card pushed to the learner monitor (Emergency Manual events).
  // null when hidden; otherwise { title, lines: string[], scenario?, index? }.
  activeAidCard: null,
  setActiveAidCard: (card) => set({ activeAidCard: card }),

  // One-shot audio cue — the instructor fires it, the MONITOR window plays it
  // (audio never plays from the instructor window). { type, id } where id is a
  // unique timestamp so repeats retrigger. type: 'defibrillation' |
  // 'cardioversion' | 'needleRelease'.
  audioCue: null,
  setAudioCue: (cue) => set({ audioCue: cue }),

  // ── Undo (instructor-local, not synced) ───────────────────────────────────
  // ScenarioSelector pushes a snapshot before every scenario / modifier change;
  // the footer's ↶ Undo restores the most recent one.
  undoStack: [],
  pushUndo: () =>
    set((state) => {
      const snap = {
        targets: Object.fromEntries(Object.entries(state.vitals).map(([k, v]) => [k, v.target ?? v.current])),
        rhythm: state.rhythm, capnoMorphology: state.capnoMorphology,
        activeScenario: state.activeScenario, activeModifier: state.activeModifier,
        ventSettings: { ...state.ventSettings }, visibleChannels: { ...state.visibleChannels },
        intubated: state.intubated, splitScreen: state.splitScreen,
      };
      return { undoStack: [...state.undoStack.slice(-9), snap] };
    }),
  undo: () => {
    const state = get();
    const snap = state.undoStack[state.undoStack.length - 1];
    if (!snap) return false;
    set({ undoStack: state.undoStack.slice(0, -1) });
    state.applyVitals(snap.targets);
    state.setRhythm(snap.rhythm);
    state.setCapnoMorphology(snap.capnoMorphology);
    state.setActiveScenario(snap.activeScenario);
    state.setActiveModifier(snap.activeModifier);
    state.setVentSettings(snap.ventSettings);
    state.setVisibleChannels(snap.visibleChannels);
    state.setIntubated(snap.intubated);
    state.setSplitScreen(snap.splitScreen);
    state.recordCaseEvent('scenario', 'Undo');
    return true;
  },

  // ── Scenario requests from outside ScenarioSelector (sticky footer) ───────
  scenarioRequest: null,   // { key, n }
  requestScenario: (key) => set((state) => ({ scenarioRequest: { key, n: (state.scenarioRequest?.n ?? 0) + 1 } })),

  // ── Instructor presence (monitor-side) ────────────────────────────────────
  // Each instructor tab stamps its payloads with a random id. A monitor that
  // hears two different ids within a few seconds warns that two panels are
  // fighting over it (they would otherwise silently overwrite each other).
  instructorsSeen: {},     // id → last-seen epoch ms
  noteInstructor: (id) =>
    set((state) => {
      if (!id) return {};
      const now = Date.now();
      const seen = { ...state.instructorsSeen, [id]: now };
      Object.keys(seen).forEach((k) => { if (now - seen[k] > 8000) delete seen[k]; });
      return { instructorsSeen: seen };
    }),

  // ── Intervention reset signal ──────────────────────────────────────────────
  // Active interventions (with their auto-revert timers) live in InterventionPanel's
  // local state, so "Normal Baseline" — which lives in ScenarioSelector — can't clear
  // them directly. Bumping this counter tells the panel to cancel every running
  // intervention and clear its shock log. Instructor-local; not synced.
  interventionResetToken: 0,
  resetInterventions: () =>
    set((state) => ({ interventionResetToken: state.interventionResetToken + 1 })),

  // ── Split-screen ventilator (GE Aisys CS2-style) ───────────────────────────
  // When on, the monitor display splits side-by-side with a ventilator screen.
  // Off at start: the baseline patient is spontaneously breathing, not
  // mechanically ventilated. The instructor turns it on (or intubates) to
  // reveal the machine.
  splitScreen: false,
  setSplitScreen: (on) => set({ splitScreen: on }),

  // ── Airway status ──────────────────────────────────────────────────────────
  // The baseline patient is spontaneously breathing — NOT intubated. Opening the
  // ventilator screen must NOT by itself imply a secured airway or delivered
  // volatile agent. This flag flips true only when:
  //   • the Endotracheal Intubation intervention is given, or
  //   • a loaded scenario dictates a mechanically ventilated patient (one that
  //     carries a `vent` airway-mechanics block, unless it sets intubated:false).
  // Extubation and returning to Normal Baseline reset it. When false, the vent
  // screen shows a STANDBY state (no agent / MAC, flat airway waves).
  intubated: false,
  setIntubated: (on) => set({ intubated: !!on }),

  // ── Pulse Pressure Variation (PPV) ─────────────────────────────────────────
  // Instructor-set respiratory swing on the ARTERIAL LINE + SpO₂ PLETH waveforms,
  // for teaching fluid-responsiveness assessment. `percent` is the approximate PPV
  // magnitude (a high PPV, >~13%, suggests the patient is fluid-responsive).
  // Independent of the scenario-driven swing (hypovolaemia / tamponade).
  ppv: { enabled: false, percent: 18 },
  setPpv: (patch) => set((state) => ({ ppv: { ...state.ppv, ...patch } })),

  // ── Depth-of-anaesthesia (processed EEG) monitor ───────────────────────────
  // A third screen beside the vitals monitor, styled as BIS / Entropy / SedLine.
  // All three read the same hypnotic index (`bisValue`, driven by the
  // sedatives + volatiles given); the brand only changes the presentation.
  eegMonitor: { enabled: false, brand: 'bis' },
  setEegMonitor: (patch) => set((state) => ({ eegMonitor: { ...state.eegMonitor, ...patch } })),

  // ── Intrapartum fetal (CTG / EFM) monitor ──────────────────────────────────
  // When on, the monitor display splits side-by-side with a cardiotocograph:
  // fetal heart rate (FHR) trace + tocodynamometer (uterine activity) trace.
  // Toggled from the OB/GYN tab; the pattern is instructor-driven.
  fetalMonitor: false,
  setFetalMonitor: (on) => set({ fetalMonitor: on }),
  fetal: {
    fhrBaseline: 140,        // bpm (CTG scale 30–240)
    variability: 'moderate', // 'absent' | 'minimal' | 'moderate' | 'marked'
    accels: true,            // periodic accelerations present
    decel: 'none',           // 'none' | 'early' | 'late' | 'variable' | 'prolonged'
    sinusoidal: false,       // smooth sine wave overriding variability (ominous)
    pattern: 'normal',       // active preset key (for control highlighting)
    contractionFreq: 3,      // contractions per 10 min (0 = quiescent)
    contractionStrength: 55, // toco peak amplitude (relative units 0–100)
    uterineTone: 12,         // toco resting baseline tone
    windowSec: 150,          // seconds of tracing visible (zoom: ↑ = longer trend)
  },
  setFetal: (patch) => set((state) => ({ fetal: { ...state.fetal, ...patch } })),

  // Ventilator settings (instructor-adjustable). Drive the vent waveforms +
  // numerics. mode: 'VCV' (volume control) | 'PCV' (pressure control) | 'PSV'.
  ventSettings: {
    mode: 'VCV',  // 'VCV' | 'PCV' | 'PSV' — governs waveform shape
    vte: 500,     // mL — expired tidal volume (directly editable)
    ppeak: 18,    // cmH₂O — peak airway pressure (directly editable)
    pplat: 15,    // cmH₂O — plateau pressure (directly editable)
    rr: 12,       // /min — set ventilator rate
    peep: 5,      // cmH₂O
    fio2: 50,     // %
    ie: '1:2',    // inspiratory:expiratory ratio
    fico2: 0,     // mmHg — inspired CO₂ (↑ with rebreathing / exhausted absorber)
    agent: 'none',// 'none' | 'sevo' | 'des' | 'iso' — vaporizer OFF until an
    agentPct: 0,  // Inhalational Agents card (or the vent controls) turns it on
    n2o: 0,       // % — delivered nitrous oxide (adds to total MAC)
    psupport: 10, // cmH₂O — pressure support
    flowTrigger: 0.6, // L/min — flow trigger sensitivity
    totalFlow: 1.0,   // L/min — fresh gas total flow
  },
  // Update a single ventilator setting key — coupled through the lung model
  // (VT/rate/PEEP/I:E move the pressures; typing a pressure re-fits the lungs).
  setVentSetting: (key, value) =>
    set((state) => ({ ventSettings: applyVentSettingChange(state.ventSettings, key, value) })),
  // Bulk-replace (scenarios, interventions, sync). Pressures are authoritative
  // here, so compliance/resistance are re-derived from them.
  setVentSettings: (settings) =>
    set({ ventSettings: { ...settings, ...deriveMechanics(settings) } }),

  // Monitor channel visibility — controls which waveform rows are shown on the display.
  // Default (baseline): a spontaneously breathing, non-invasively monitored patient —
  // only ECG, SpO₂ and NIBP (always rendered). ABP (art line), CVP (central line),
  // EtCO₂/capnography (intubation), Temp, BIS and TOF are revealed by the instructor
  // (or by interventions, e.g. intubation enables EtCO₂) as the case escalates.
  visibleChannels: { abp: false, cvp: false, etco2: false, temp: false, tof: false, bis: false },
  // Toggle a single channel on/off (used by instructor panel toggles)
  setChannelVisible: (channel, visible) =>
    set((state) => ({
      visibleChannels: { ...state.visibleChannels, [channel]: visible },
    })),
  // Bulk-replace the full visibility map (used by BroadcastChannel subscriber)
  setVisibleChannels: (channels) => set({ visibleChannels: channels }),

  // NIBP cycling state — true while the cuff is inflated/measuring
  nibpCycling: false,
  setNibpCycling: (cycling) => set({ nibpCycling: cycling }),

  // Last successfully measured NIBP — only updated when a cycle completes.
  // Monitor always displays this value, not the live interpolated vital.
  nibpLastMeasured: { sys: 120, dia: 80 },
  setNibpLastMeasured: (sys, dia) => set({ nibpLastMeasured: { sys, dia } }),

  // ── Custom scenarios (instructor-built, localStorage; NOT synced) ──────────
  // Applying one sets the already-synced rhythm/vitals/channels/vent, so the
  // monitor never needs the definition. Persisted so they survive reloads and
  // can be exported/imported as JSON to share.
  customScenarios: loadCustomScenarios(),
  addCustomScenario: (scn) =>
    set((state) => {
      const next = [...state.customScenarios.filter((c) => c.key !== scn.key), scn];
      saveCustomScenarios(next);
      return { customScenarios: next };
    }),
  deleteCustomScenario: (key) =>
    set((state) => {
      const next = state.customScenarios.filter((c) => c.key !== key);
      saveCustomScenarios(next);
      return { customScenarios: next };
    }),
  // Merge imported scenarios (de-duped by key), keeping existing ones.
  mergeCustomScenarios: (list) =>
    set((state) => {
      const byKey = new Map(state.customScenarios.map((c) => [c.key, c]));
      list.forEach((c) => byKey.set(c.key, c));
      const next = [...byKey.values()];
      saveCustomScenarios(next);
      return { customScenarios: next };
    }),

  // Sync mode and connection status
  syncMode: 'broadcast', // 'broadcast' or 'websocket'
  setSyncMode: (mode) => set({ syncMode: mode }),
  isConnected: true,
  setIsConnected: (connected) => set({ isConnected: connected }),

  // ── Case recording (session-only; NOT synced) ──────────────────────────────
  // A "case" is an instructor-controlled recording session. While active, the
  // vitals are sampled periodically and scenario/intervention events are logged,
  // each timestamped from case start (ms). Deliberately kept OUT of the sync
  // payload (see useBroadcastSync.buildPayload) so the growing arrays never
  // cross to the monitor or bloat broadcasts. Lives only in the instructor's
  // store instance. Cleared when a new case starts; downloadable on End Case.
  caseRecording: {
    active: false,
    startedAt: null,   // epoch ms
    endedAt: null,     // epoch ms
    samples: [],       // [{ t, HR, SpO2, NIBP_Sys, NIBP_Dia, ABP_Sys, ABP_Dia, EtCO2, RR, Temp, CVP, BIS, TOF }]
    events: [],        // [{ t, kind, label }]  kind: 'scenario' | 'modifier' | 'intervention' | 'shock'
  },
  startCase: () =>
    set({ caseRecording: { active: true, startedAt: Date.now(), endedAt: null, samples: [], events: [] } }),
  endCase: () =>
    set((state) => (state.caseRecording.active
      ? { caseRecording: { ...state.caseRecording, active: false, endedAt: Date.now() } }
      : {})),
  resetCase: () =>
    set({ caseRecording: { active: false, startedAt: null, endedAt: null, samples: [], events: [] } }),
  // Apply just the case-timer state (active + startedAt) from sync. The monitor
  // uses this to show the same elapsed clock; samples/events stay instructor-only.
  setCaseTimer: (active, startedAt) =>
    set((state) => ({ caseRecording: { ...state.caseRecording, active: !!active, startedAt: startedAt ?? null } })),
  // Push a timestamped snapshot of the current numerics (no-op unless recording).
  // Only records channels that are ACTIVE on the monitor — HR / SpO₂ / NIBP are
  // always shown; ABP, EtCO₂+RR, Temp, CVP, BIS and TOF are recorded only while
  // their channel is enabled (via visibleChannels). A channel enabled mid-case
  // (e.g. an art line inserted → ABP) simply begins recording from that sample;
  // earlier samples stay blank (null).
  recordCaseSample: () =>
    set((state) => {
      const rec = state.caseRecording;
      if (!rec.active) return {};
      const v = state.vitals;
      const vis = state.visibleChannels || {};
      const r = (x) => (typeof x === 'number' ? Math.round(x * 10) / 10 : null);
      const g = (on, x) => (on ? r(x) : null);   // gated by channel visibility
      const sample = {
        t: Date.now() - rec.startedAt,
        HR: r(v.HR?.current),             SpO2: r(v.SpO2?.current),          // ECG + pleth: always on
        NIBP_Sys: r(v.NIBP_Sys?.current), NIBP_Dia: r(v.NIBP_Dia?.current),  // NIBP: always on
        ABP_Sys: g(vis.abp, v.ABP_Sys?.current),   ABP_Dia: g(vis.abp, v.ABP_Dia?.current),
        EtCO2: g(vis.etco2, v.EtCO2?.current),     RR: g(vis.etco2, v.RR?.current), // RR shown in the CO₂ tile
        Temp: g(vis.temp, v.Temp?.current),        CVP: g(vis.cvp, v.CVP?.current),
        BIS: g(vis.bis, state.bisValue),           TOF: g(vis.tof, state.tofCount),
        // Capnograph shape + ventilator screen state, so replay can reconstruct
        // the EtCO₂ morphology and the ventilator screen. `vent` only when shown.
        capno: state.capnoMorphology,
        split: !!state.splitScreen,
        intubated: !!state.intubated,
        vent: state.splitScreen ? { ...state.ventSettings } : null,
        // What the learners were looking at: pushed cognitive-aid card / media.
        aid: state.activeAidCard ?? null,
        media: state.activeMedia ?? null,
      };
      return { caseRecording: { ...rec, samples: [...rec.samples, sample] } };
    }),
  // Log a timestamped event (no-op unless recording).
  // Log a timestamped case event (scenario / modifier / intervention / shock).
  // `t` = ms elapsed since case start (debrief timeline); `at` = absolute epoch
  // (wall-clock, correlates with the monitor's shock times).
  recordCaseEvent: (kind, label) =>
    set((state) => {
      const rec = state.caseRecording;
      if (!rec.active || !label) return {};
      const now = Date.now();
      return { caseRecording: { ...rec, events: [...rec.events, { t: now - rec.startedAt, at: now, kind, label }] } };
    }),
}));

// ── Instructor preferences persistence ───────────────────────────────────────
// Alarm limits, transition speed and patient demographics survive a tab
// refresh / reopen (localStorage). Vitals values themselves are NOT persisted —
// a fresh page is a fresh patient; only the instructor's setup is kept.
const PREFS_KEY = 'sim.instructorPrefs';
export function loadInstructorPrefs() {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || 'null'); } catch { return null; }
}
export function applyInstructorPrefs(prefs = loadInstructorPrefs()) {
  if (!prefs) return;
  const s = useSimStore.getState();
  if (typeof prefs.transitionDuration === 'number') s.setTransitionDuration(prefs.transitionDuration);
  Object.entries(prefs.alarms ?? {}).forEach(([k, a]) => {
    if (s.vitals[k] && typeof a?.alarmLow === 'number' && typeof a?.alarmHigh === 'number') s.setAlarmThresholds(k, a);
  });
  ['patientName', 'patientId', 'patientAge'].forEach((f) => { if (prefs[f] != null) s.setPatientField(f, prefs[f]); });
}
export function startInstructorPrefsPersistence() {
  if (typeof localStorage === 'undefined') return () => {};
  let timer = null;
  return useSimStore.subscribe(() => {
    if (timer) return;                 // throttle — transitions tick every 50 ms
    timer = setTimeout(() => {
      timer = null;
      const state = useSimStore.getState();
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify({
          transitionDuration: state.transitionDuration,
          alarms: Object.fromEntries(Object.entries(state.vitals).map(([k, v]) => [k, { alarmLow: v.alarmLow, alarmHigh: v.alarmHigh }])),
          patientName: state.patientName, patientId: state.patientId, patientAge: state.patientAge,
        }));
      } catch { /* private mode / quota — prefs just don't persist */ }
    }, 1000);
  });
}
